import { calculatePrice } from '@funkey/shared/activities';
import type { ActivityPriceType } from '@funkey/constants';
import type { PartnerOption } from '../data/getGroupPartners';

/**
 * Per-person price for a variant at a given participant count, using the
 * canonical pricing engine so the widget matches the PDP exactly across pricing
 * models. PER_PARTICIPANT is priced per head; RANGE and SCALE are group-priced,
 * so the engine divides the group total by the participant count — this is what
 * keeps "vanaf €X p.p." sane for group-priced activities instead of showing the
 * whole-group total as a per-person figure.
 */
export function variantPricePerPerson(
    variant: PartnerOption['variants'][number],
    participants: number,
    priceType: ActivityPriceType,
): number {
    return calculatePrice({
        participants,
        priceType,
        variant: {
            extraCost: variant.extraCost,
            extraCostPerCount: variant.extraCostPerCount,
            priceRules: variant.priceRules,
        },
    }).pricePerParticipant;
}

/**
 * Min/max per-person price across a partner's variants for the given
 * participant count. Used by the map's "Vanaf …" label so it matches what the
 * variant list shows for the same bracket.
 */
export function getPartnerPriceRange(
    partner: PartnerOption,
    participants: number,
): { min: number | null; max: number | null } {
    let min: number | null = null;
    let max: number | null = null;
    for (const variant of partner.variants) {
        const price = variantPricePerPerson(variant, participants, partner.priceType);
        if (!Number.isFinite(price) || price <= 0) continue;
        if (min === null || price < min) min = price;
        if (max === null || price > max) max = price;
    }
    return { min, max };
}
